import { type Transaction } from "@finway-group/shared/lib/models"
import { type ActionCreator, type Dispatch } from "redux"
import { type TransactionTabTypeEnum } from "~Features/pages/transactions/TransactionsList"
import { invalidateTransaction } from "~Shared/queries/transaction.queries"
import { TransactionService } from "~Shared/services"
import { type ThunkResult } from "~Shared/store"
import {
    type DeleteManyTransactionsAction,
    type DeleteTransactionAction,
    type FetchAllTransactionAction,
    type SetActiveTransactionTabAction,
    type SetShouldRefetchTransactionsAction,
    TransactionActionTypes,
} from "~Shared/store/actions/transaction/transactionTypes"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { setShouldFetchCashFlowsData } from "../liquidity/liquidityActions"
import { refetchTableIfNecessary } from "../tables/tableActions"

// prettier-ignore
export const fetchAllTransactions: ActionCreator<ThunkResult<Transaction>> = (page: number = 1, limit: number = 20, sortingCriteria = '', query = '') => async (dispatch: Dispatch) => {
    const { transactions, totalPages, totalDocs } = await TransactionService.fetchMatchingTransactions(page, limit, sortingCriteria, query);

    const fetchAllTransaction: FetchAllTransactionAction = {
        type: TransactionActionTypes.FETCH_ALL_TRANSACTION,
        transactions,
        page,
        limit,
        totalPages,
        totalDocs,
    };

    dispatch(fetchAllTransaction);

    // reset refetch flag
    setShouldRefetch(false)(dispatch);

    return transactions;
};

export const deleteTransaction = (id: string) => async (dispatch: Dispatch) => {
    await TransactionService.deleteTransaction(id)
    const deleteTransactionAction: DeleteTransactionAction = {
        type: TransactionActionTypes.DELETE_TRANSACTION,
        transactionId: id,
    }

    refetchTableIfNecessary(TablesEnum.CARD_TRANSACTIONS, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_INFLOW, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_OUTFLOW, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_ALL, [id])
    refetchTableIfNecessary(TablesEnum.EXPENSE_TRANSACTIONS, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_ARCHIVE, [id])

    dispatch(deleteTransactionAction)
}

export const deleteManyTransactions = (ids: Array<string>) => async (dispatch: Dispatch) => {
    await TransactionService.deleteManyTransactions(ids)
    const deleteTransactionsAction: DeleteManyTransactionsAction = {
        type: TransactionActionTypes.DELETE_MANY_TRANSACTIONS,
        transactionIds: ids,
    }

    refetchTableIfNecessary(TablesEnum.CARD_TRANSACTIONS, ids)
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_INFLOW, ids)
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_OUTFLOW, ids)
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_ALL, ids)
    refetchTableIfNecessary(TablesEnum.EXPENSE_TRANSACTIONS, ids)
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_ARCHIVE, ids)

    dispatch(deleteTransactionsAction)
}

export const syncTransaction = async (id: string) => {
    const transaction = await invalidateTransaction(id)

    refetchTableIfNecessary(TablesEnum.CARD_TRANSACTIONS, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_INFLOW, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_OUTFLOW, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_ALL, [id])
    refetchTableIfNecessary(TablesEnum.EXPENSE_TRANSACTIONS, [id])
    refetchTableIfNecessary(TablesEnum.TRANSACTIONS_ARCHIVE, [id])

    return transaction
}

export const setShouldRefetch = (shouldRefetch: boolean) => (dispatch: Dispatch) => {
    const setShouldRefetchAction: SetShouldRefetchTransactionsAction = {
        type: TransactionActionTypes.SET_SHOULD_REFETCH_TRANSACTIONS,
        shouldRefetch,
    }

    dispatch(setShouldRefetchAction)
}

export const updateTransactionCategoryField = (transactionId: string, transactionCategoryId: string) => async (dispatch: Dispatch) => {
    const transaction = await TransactionService.updateTransactionCategory(transactionId, transactionCategoryId)

    syncTransaction(transaction.id)
    setShouldFetchCashFlowsData(true)(dispatch)

    return transaction
}

export const setActiveTransactionTab: ActionCreator<void> = (activeTab: TransactionTabTypeEnum) => (dispatch: Dispatch) => {
    const setActiveTransactionTabAction: SetActiveTransactionTabAction = {
        type: TransactionActionTypes.SET_ACTIVE_TRANSACTION_TAB,
        activeTab,
    }

    dispatch(setActiveTransactionTabAction)
}
